import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Search,
  Fingerprint,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Plus,
  Link2,
  Users,
  Building2,
  FileText,
  Copy,
  Check,
  Sparkles,
  TrendingUp,
  Shield,
  Globe,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Types
interface PIDRecord {
  id: string;
  type: 'doi' | 'orcid' | 'ror';
  identifier: string;
  title: string;
  status: 'valid' | 'invalid' | 'pending';
  linkedEntities: number;
  lastVerified: string;
  createdAt: string;
}

// Sample PID data
const samplePIDs: PIDRecord[] = [
  {
    id: '1',
    type: 'doi',
    identifier: '10.25919/5c92e4bs1d7a',
    title: 'Great Barrier Reef Temperature Dataset 2020-2024',
    status: 'valid',
    linkedEntities: 12,
    lastVerified: '2026-01-15',
    createdAt: '2024-03-15',
  },
  {
    id: '2',
    type: 'doi',
    identifier: '10.26180/21838754',
    title: 'Climate Change Impacts on Marine Biodiversity',
    status: 'valid',
    linkedEntities: 8,
    lastVerified: '2026-01-14',
    createdAt: '2023-11-20',
  },
  {
    id: '3',
    type: 'orcid',
    identifier: '0000-0002-1825-0097',
    title: 'Dr. Sarah Chen - Marine Biologist',
    status: 'valid',
    linkedEntities: 24,
    lastVerified: '2026-01-15',
    createdAt: '2022-06-10',
  },
  {
    id: '4',
    type: 'orcid',
    identifier: '0000-0001-5109-3700',
    title: 'Prof. James Wilson - Climate Scientist',
    status: 'valid',
    linkedEntities: 42,
    lastVerified: '2026-01-14',
    createdAt: '2021-09-05',
  },
  {
    id: '5',
    type: 'ror',
    identifier: '03fy7b149',
    title: 'CSIRO - Commonwealth Scientific and Industrial Research Organisation',
    status: 'valid',
    linkedEntities: 156,
    lastVerified: '2026-01-15',
    createdAt: '2023-01-01',
  },
  {
    id: '6',
    type: 'ror',
    identifier: '01ej9dk98',
    title: 'University of Melbourne',
    status: 'valid',
    linkedEntities: 142,
    lastVerified: '2026-01-15',
    createdAt: '2023-01-01',
  },
  {
    id: '7',
    type: 'doi',
    identifier: '10.4225/08/abc123',
    title: 'Coral Bleaching Survey Methods',
    status: 'pending',
    linkedEntities: 3,
    lastVerified: '2026-01-10',
    createdAt: '2025-12-20',
  },
  {
    id: '8',
    type: 'orcid',
    identifier: '0000-0003-xxxx-xxxx',
    title: 'Dr. Michael Brown - Research Fellow',
    status: 'invalid',
    linkedEntities: 0,
    lastVerified: '2026-01-12',
    createdAt: '2024-08-15',
  },
];

// Statistics
const pidStats = {
  totalDOIs: 1247,
  totalORCIDs: 892,
  totalRORs: 45,
  validationRate: 98.5,
  autoAssigned: 78,
  pendingVerification: 23,
  invalidCount: 12,
  linkedResources: 4521,
};

// PID Type configuration
const pidTypeConfig = {
  doi: {
    label: 'DOI',
    fullName: 'Digital Object Identifier',
    color: 'bg-blue-500',
    textColor: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    icon: FileText,
    prefix: 'https://doi.org/',
  },
  orcid: {
    label: 'ORCID',
    fullName: 'Open Researcher and Contributor ID',
    color: 'bg-green-500',
    textColor: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-950/30',
    icon: Users,
    prefix: 'https://orcid.org/',
  },
  ror: {
    label: 'ROR',
    fullName: 'Research Organization Registry',
    color: 'bg-purple-500',
    textColor: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-950/30',
    icon: Building2,
    prefix: 'https://ror.org/',
  },
};

const statusConfig = {
  valid: { label: 'Valid', color: 'bg-green-500', icon: CheckCircle2 },
  invalid: { label: 'Invalid', color: 'bg-red-500', icon: XCircle },
  pending: { label: 'Pending', color: 'bg-yellow-500', icon: AlertCircle },
};

export function ResearchPIDsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedPID, setSelectedPID] = useState<PIDRecord | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);

  // Filter PIDs
  const filteredPIDs = samplePIDs.filter(pid => {
    const matchesSearch = pid.identifier.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         pid.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || pid.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || pid.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleCopy = async (identifier: string, id: string) => {
    await navigator.clipboard.writeText(identifier);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Fingerprint className="h-8 w-8 text-primary" />
            Persistent Identifiers
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage DOIs, ORCIDs, and ROR identifiers for research attribution
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Verify All
          </Button>
          <Button size="sm" onClick={() => setRegisterDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Register New PID
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <Card className="col-span-2">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <FileText className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pidStats.totalDOIs.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">DOIs Registered</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-2">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <Users className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pidStats.totalORCIDs.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">ORCIDs Linked</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-2">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Building2 className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pidStats.totalRORs}</p>
                <p className="text-xs text-muted-foreground">ROR IDs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-2">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pidStats.validationRate}%</p>
                <p className="text-xs text-muted-foreground">Validation Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all" className="gap-2">
            <Link2 className="h-4 w-4" />
            All PIDs
          </TabsTrigger>
          <TabsTrigger value="dois" className="gap-2">
            <FileText className="h-4 w-4" />
            DOIs
          </TabsTrigger>
          <TabsTrigger value="orcids" className="gap-2">
            <Users className="h-4 w-4" />
            ORCIDs
          </TabsTrigger>
          <TabsTrigger value="rors" className="gap-2">
            <Building2 className="h-4 w-4" />
            RORs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by identifier or title..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="doi">DOI</SelectItem>
                    <SelectItem value="orcid">ORCID</SelectItem>
                    <SelectItem value="ror">ROR</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="valid">Valid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="invalid">Invalid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* PID Table */}
          <Card>
            <CardHeader>
              <CardTitle>PID Registry</CardTitle>
              <CardDescription>
                {filteredPIDs.length} identifiers found
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Identifier</TableHead>
                    <TableHead>Title / Entity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Linked</TableHead>
                    <TableHead>Last Verified</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPIDs.map((pid) => {
                    const typeConfig = pidTypeConfig[pid.type];
                    const StatusIcon = statusConfig[pid.status].icon;

                    return (
                      <TableRow key={pid.id}>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={cn('gap-1', typeConfig.bgColor, typeConfig.textColor)}
                          >
                            <typeConfig.icon className="h-3 w-3" />
                            {typeConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                              {pid.identifier}
                            </code>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleCopy(pid.identifier, pid.id)}
                            >
                              {copiedId === pid.id ? (
                                <Check className="h-3 w-3 text-green-500" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[300px]">
                          <p className="truncate text-sm font-medium">{pid.title}</p>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={cn(
                              'gap-1',
                              pid.status === 'valid' && 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                              pid.status === 'pending' && 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
                              pid.status === 'invalid' && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            )}
                          >
                            <StatusIcon className="h-3 w-3" />
                            {statusConfig[pid.status].label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{pid.linkedEntities}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {pid.lastVerified}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setSelectedPID(pid)}
                            >
                              <Search className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              asChild
                            >
                              <a
                                href={`${typeConfig.prefix}${pid.identifier}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dois">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-500" />
                DOI Management
              </CardTitle>
              <CardDescription>
                Digital Object Identifiers for datasets and publications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">1,247</p>
                  <p className="text-sm text-muted-foreground">Total DOIs</p>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg">
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">98.2%</p>
                  <p className="text-sm text-muted-foreground">Resolution Rate</p>
                </div>
                <div className="p-4 bg-orange-50 dark:bg-orange-950/30 rounded-lg">
                  <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">156</p>
                  <p className="text-sm text-muted-foreground">Registered This Month</p>
                </div>
              </div>
              <Button className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Register New DOI via DataCite
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="orcids">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-green-500" />
                ORCID Integration
              </CardTitle>
              <CardDescription>
                Connect researchers to their ORCID profiles
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg">
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">892</p>
                  <p className="text-sm text-muted-foreground">Linked ORCIDs</p>
                </div>
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">78%</p>
                  <p className="text-sm text-muted-foreground">Auto-Linked</p>
                </div>
                <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">45</p>
                  <p className="text-sm text-muted-foreground">Pending Verification</p>
                </div>
              </div>
              <Button className="w-full" variant="outline">
                <Globe className="h-4 w-4 mr-2" />
                Connect ORCID Account
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rors">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-purple-500" />
                ROR Registry
              </CardTitle>
              <CardDescription>
                Research Organization Registry identifiers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">45</p>
                  <p className="text-sm text-muted-foreground">Institutions</p>
                </div>
                <div className="p-4 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg">
                  <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">100%</p>
                  <p className="text-sm text-muted-foreground">Validation Rate</p>
                </div>
                <div className="p-4 bg-pink-50 dark:bg-pink-950/30 rounded-lg">
                  <p className="text-2xl font-bold text-pink-600 dark:text-pink-400">4,521</p>
                  <p className="text-sm text-muted-foreground">Linked Resources</p>
                </div>
              </div>
              <Button className="w-full" variant="outline">
                <Search className="h-4 w-4 mr-2" />
                Search ROR Registry
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* AI Suggestions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI PID Suggestions
          </CardTitle>
          <CardDescription>
            Automatically detected identifiers that may need attention
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4 text-yellow-500" />
                <span className="font-medium text-sm">Missing ORCIDs</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                23 researchers have publications without linked ORCID profiles
              </p>
              <Button size="sm" variant="outline" className="w-full">
                Review & Link
              </Button>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-blue-500" />
                <span className="font-medium text-sm">DOI Candidates</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                15 datasets are eligible for DOI registration
              </p>
              <Button size="sm" variant="outline" className="w-full">
                Register DOIs
              </Button>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="h-4 w-4 text-purple-500" />
                <span className="font-medium text-sm">Institution Matches</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                8 institution names could be linked to ROR IDs
              </p>
              <Button size="sm" variant="outline" className="w-full">
                Link RORs
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Register Dialog */}
      <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Register New PID</DialogTitle>
            <DialogDescription>
              Register a new persistent identifier for a research resource
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">PID Type</label>
              <Select defaultValue="doi">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="doi">DOI (DataCite)</SelectItem>
                  <SelectItem value="orcid">ORCID (Link Existing)</SelectItem>
                  <SelectItem value="ror">ROR (Link Existing)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Resource Title</label>
              <Input placeholder="Enter resource title..." />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Resource Type</label>
              <Select defaultValue="dataset">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dataset">Dataset</SelectItem>
                  <SelectItem value="publication">Publication</SelectItem>
                  <SelectItem value="software">Software</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegisterDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setRegisterDialogOpen(false)}>
              Register PID
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ResearchPIDsPage;
